import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useSyncExternalStore } from 'react';
import { isOwnUser, StreamChat } from 'stream-chat';

import { streamUnreadCountAtom } from '@/features/chat/atoms/streamUnreadCountAtom';
import { useIsStreamEnabled } from '@/features/chat/hooks/useGetStreamConfig';
import { useMyUserId } from '@/features/users/hooks/useGetMyUser';
import { useTokenProvider } from '@/integrations/getstream/hooks/useTokenProvider';
import { getStreamApiKey } from '@/integrations/getstream/utils/getStreamApiKey';

const chatClient = new StreamChat(getStreamApiKey(), {
  timeout: 15_000,
});

export function useStreamChatClient() {
  return chatClient;
}

export function useIsStreamChatClientConnected() {
  const client = useStreamChatClient();

  return useSyncExternalStore(
    callback => {
      client.on('connection.changed', callback);
      return () => {
        client.off('connection.changed', callback);
      };
    },
    () => !!client.userID,
  );
}

export function useConnectToStreamChat() {
  const isStreamEnabled = useIsStreamEnabled();
  const setUnreadCount = useSetAtom(streamUnreadCountAtom);
  const userId = useMyUserId();
  const tokenProvider = useTokenProvider();

  useEffect(() => {
    if (!isStreamEnabled) return;
    const connectionPromise = chatClient.connectUser(
      { id: userId },
      tokenProvider,
    );

    void connectionPromise.then(() => {
      const unreadCount =
        chatClient.user && isOwnUser(chatClient.user)
          ? chatClient.user.total_unread_count
          : 0;
      setUnreadCount(unreadCount);
    });

    return () => {
      void connectionPromise.then(() => chatClient.disconnectUser());
    };
  }, [isStreamEnabled, setUnreadCount, userId, tokenProvider]);
}
